/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text } from '@components/atoms'
import { Modal } from '@components/molecules'
import { Footer } from '@components/systems'
import { customerAuthStyle } from './customerAuth.style'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { useNavigate } from 'react-router-dom'
import { Validator, ValidatorResultType } from '@validators'

/**
 * Reset password — Monolith(2안).
 *
 * 검증(Validator.customer)·발송(FORGOT_PASSWORD)·결과 모달은 구 페이지 그대로다.
 * DESIGN_SYSTEM §6.4 "결과는 기존 모달" 규칙에 따라 모달은 교체하지 않았다.
 */
const MonolithCustomerForgotPwPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// hook
	const navigate = useNavigate()

	// redux state
	const forgotPasswordErrorMessage: string = useAppSelector(
		state => state.customerAuth.forgotPasswordErrorMessage,
	)
	const showForgotPasswordModal: boolean = useAppSelector(
		state => state.customerAuth.showForgotPasswordModal,
	)

	// state
	const [email, setEmail] = React.useState<string>('')

	const onSendToEmailClick = React.useCallback(() => {
		const res: ValidatorResultType = Validator.customer({
			email: email,
		})

		if (!res.email.isValid) {
			reduxDispatch(customerAuthActions.FORGOT_PASSWORD_ERROR_MESSAGE(res.email.message))
		} else {
			reduxDispatch(customerAuthActions.FORGOT_PASSWORD({ email: email }))
		}
	}, [email])

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		onSendToEmailClick()
	}

	return (
		<View css={style}>
			<View className="mnWrap">
				<View className="mnAuthHeader">
					<h1 className="mnAuthTitle">Reset password</h1>
					<p className="mnAuthLede">
						Enter the email on your account. We send a reset link to it.
					</p>
				</View>

				<form className="mnAuthForm" onSubmit={onSubmit} noValidate>
					<View className="mnField">
						<label className="mnLabel" htmlFor="mnForgotEmail">
							Email
						</label>
						<input
							id="mnForgotEmail"
							type="email"
							className={`mnInput ${forgotPasswordErrorMessage !== '' ? 'error' : ''}`}
							value={email}
							onChange={e => setEmail(e.target.value)}
							autoComplete="email"
						/>
						{forgotPasswordErrorMessage !== '' && (
							<span className="mnError">{forgotPasswordErrorMessage}</span>
						)}
					</View>

					<View className="mnAuthAction">
						<button type="submit" className="mnCta mnAuthCta">
							Send reset link
						</button>
					</View>

					<View className="mnAuthLinks">
						<button
							type="button"
							className="mnAuthLink"
							onClick={() => navigate('/customer/login')}
						>
							Back to login
						</button>
					</View>
				</form>
			</View>

			<Footer />

			<Modal
				target="newGroup"
				title="Password reset email sent"
				show={showForgotPasswordModal}
				onCloseModal={() => {
					reduxDispatch(customerAuthActions.SHOW_FORGOT_PASSWORD_MODAL(false))
					navigate('/customer/login')
				}}
				closeStatus="inherit"
				showSubmit={false}
				closeTitle="Close"
			>
				<View css={{ width: '30rem', maxWidth: '80vw' }}>
					<Text category="p2">An email has been sent to reset your password.</Text>
					<Text category="p2">Please check the email you entered.</Text>
					<Text category="p2">
						If you are unable to receive the email, please contact us via CONTACT.
					</Text>
				</View>
			</Modal>
		</View>
	)
}

export default MonolithCustomerForgotPwPage

const style = css(customerAuthStyle)
